import { useState, useEffect } from "react";
import { toast } from "sonner";
import { HandHeart, CircleNotch } from "@phosphor-icons/react";
import { api } from "@/lib/api";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const DEFAULT_PACKAGES = [
  { id: "warm_meal", amount: 10, label: "A Warm Meal", description: "Feeds one guest a freshly cooked dinner." },
  { id: "hygiene_kit", amount: 25, label: "Hygiene Kit", description: "Soap, toothbrush, towel & essentials." },
  { id: "evening_table", amount: 50, label: "An Evening Table", description: "Hosts a table of five for the evening." },
  { id: "community_circle", amount: 100, label: "Community Circle", description: "Supports a full week of evenings." },
];

export default function DonationDialog({ open, onOpenChange }) {
  const [packages, setPackages] = useState(DEFAULT_PACKAGES);
  const [selected, setSelected] = useState("evening_table");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/donations/packages")
      .then((r) => setPackages(r.data.packages))
      .catch(() => {});
  }, []);

  const pickPackage = (id) => {
    setSelected(id);
    setCustomAmount("");
  };

  const pickCustom = () => {
    setSelected("custom");
  };

  const handleDonate = async () => {
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
      };
      if (selected === "custom") {
        const amt = parseFloat(customAmount);
        if (!amt || amt < 1) {
          toast.error("Please enter at least $1");
          setLoading(false);
          return;
        }
        payload.custom_amount = amt;
      } else {
        payload.package_id = selected;
      }
      const { data } = await api.post("/donations/checkout", payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="donation-dialog"
        className="bg-[#FDFBF7] border border-[#EADAB9] rounded-3xl max-w-2xl p-0 overflow-hidden"
      >
        <div className="p-8 md:p-10">
          <DialogHeader className="text-left">
            <p className="label-eyebrow mb-3">Give a little warmth</p>
            <DialogTitle className="font-headline font-light text-3xl md:text-4xl text-[#1F1A17] leading-tight">
              Set a place at the <span className="italic text-[#C25934]">table</span>.
            </DialogTitle>
            <DialogDescription className="text-[#5A524A] text-base mt-2">
              Every dollar becomes a meal, a kit, or a quiet seat by the heater.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
            {packages.map((p) => (
              <button
                key={p.id}
                data-testid={`donation-amount-${p.id}`}
                onClick={() => pickPackage(p.id)}
                className={`text-left rounded-2xl border p-4 transition-all duration-200 ${
                  selected === p.id
                    ? "border-[#C25934] bg-[#C25934]/5 shadow-[0_0_0_2px_rgba(194,89,52,0.15)]"
                    : "border-[#EADAB9] bg-[#FDFBF7] hover:border-[#C25934]/60"
                }`}
              >
                <div className="font-headline text-2xl text-[#1F1A17]">${p.amount}</div>
                <div className="text-xs font-semibold text-[#5C6B51] uppercase tracking-wider mt-1">
                  {p.label}
                </div>
                <p className="text-xs text-[#5A524A] mt-2 leading-snug">{p.description}</p>
              </button>
            ))}
          </div>

          <div className="mt-4">
            <button
              data-testid="donation-amount-custom"
              onClick={pickCustom}
              className={`w-full text-left rounded-2xl border p-4 transition-all ${
                selected === "custom"
                  ? "border-[#C25934] bg-[#C25934]/5"
                  : "border-[#EADAB9] bg-[#FDFBF7] hover:border-[#C25934]/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="font-headline text-xl text-[#1F1A17]">Custom amount</span>
                <span className="text-sm text-[#5A524A]">— give what feels right</span>
              </div>
              {selected === "custom" && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-2xl font-headline text-[#1F1A17]">$</span>
                  <input
                    data-testid="donation-custom-input"
                    type="number"
                    min="1"
                    step="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="35"
                    className="hearth-input max-w-[160px]"
                    autoFocus
                  />
                </div>
              )}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              data-testid="donation-donor-name"
              type="text"
              placeholder="Your name (optional)"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
              className="hearth-input"
            />
            <input
              data-testid="donation-donor-email"
              type="email"
              placeholder="Email (optional, for receipt)"
              value={donorEmail}
              onChange={(e) => setDonorEmail(e.target.value)}
              className="hearth-input"
            />
          </div>

          <button
            data-testid="donation-submit"
            onClick={handleDonate}
            disabled={loading}
            className="btn-hearth mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#C25934] hover:bg-[#A04728] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-base font-semibold"
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                Opening secure checkout…
              </>
            ) : (
              <>
                <HandHeart size={20} weight="duotone" />
                Continue to secure payment
              </>
            )}
          </button>
          <p className="text-xs text-[#5A524A]/80 text-center mt-4">
            Payments processed securely by Stripe. You'll be redirected to complete your gift.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
